import { useEffect } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { tKeys } from '@/i18n';

import { useSettings } from '@/hooks/useSettings';

import { selectBracket, SET_ONBOARDING_STEP } from '../../bracket.slice';
import { Match } from '../../components/Match';
import { SecondStepText } from '../../components/OnboardingOverlay/SecondStepText';
import { hideExtraTeams } from '../../components/OnboardingOverlay/hideExtraTeams';
import { StatusBar } from '../../components/StatusBar';
import { UI_SETTINGS } from '../../helpers/constants';
import { getMatchesByRounds } from '../../helpers/getMatchesByRounds';
import { useBracketSettings } from '../../hooks/useBracketSettings';

// Should visible for user if firs time use bracket
export const OnboardingOverlay = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isMobile } = useSettings();
  const { isOnboardingActive, onboardingComplete } = useBracketSettings();

  const { seriesResults, onboardingStep: step } = useSelector(selectBracket);
  const { roundOneWest, roundTwoWest } = getMatchesByRounds(seriesResults);

  const setStep = (value: 0 | 1 | 2) => dispatch(SET_ONBOARDING_STEP(value));

  useEffect(() => {
    if (!isOnboardingActive && !onboardingComplete) setStep(1);
  }, [isOnboardingActive, onboardingComplete]);

  useEffect(() => {
    if (isOnboardingActive) {
      hideExtraTeams(step);
    }
  }, [isOnboardingActive, step, seriesResults]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Check if the clicked element is a descendant of the div with ID "OnboardingOverlayOne"
      const onboardingOverlay = document.getElementById('OnboardingOverlayOne');
      const isDescendant = onboardingOverlay?.contains(event.target as Node);

      // Check if the clicked element is a descendant of the button with ID "team"
      const button1 = document.querySelector('button#match_101_team_1');
      const button2 = document.querySelector('button#match_101_team_2');
      const isButtonDescendant =
        button1?.contains(event.target as Node) ||
        button2?.contains(event.target as Node);

      // If the clicked element is not a descendant of the div, hide the component
      if (isDescendant && isButtonDescendant) {
        // Hide the component or perform any other action
        setStep(2);
      }
    };

    // Attach the click event listener to the document
    document.addEventListener('click', handleClick);

    // Cleanup: Remove the event listener when the component is unmounted
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  if (!isOnboardingActive) return null;

  return (
    <>
      {/* Dark overlay */}
      <Box
        bgColor='#000000CC'
        h={'100%'}
        position='absolute'
        w={'100%'}
        zIndex={3}
      />

      {/* Content */}
      {
        <Box
          h={'100%'}
          id='OnboardingOverlayOne'
          position='absolute'
          top={['0', '170px']}
          w={['100%']}
          zIndex={4}
        >
          {/* Part of UI components/Bracket */}
          <Flex
            align='center'
            gap='20px'
            overflow={['hidden', 'auto']}
            p={['50px 10px 80px', '20px']}
            position='relative'
          >
            <Flex
              direction='column'
              flex={1}
              gap='10px'
              minW={UI_SETTINGS.COL_MIN_W}
            >
              {roundOneWest.map((round) => (
                <Match key={round?.id} {...round} />
              ))}
            </Flex>

            <Flex
              direction='column'
              flex={1}
              gap='10px'
              minW={UI_SETTINGS.COL_MIN_W}
            >
              {roundTwoWest.map((round) => (
                <Match key={round.id} {...round} />
              ))}
            </Flex>

            <Flex
              direction='column'
              flex={1}
              gap='10px'
              minW={UI_SETTINGS.COL_MIN_W}
            ></Flex>

            <Flex
              direction='column'
              flex={2}
              gap='10px'
              minW={UI_SETTINGS.COL_MIN_W_FINAL}
            ></Flex>

            <Flex
              direction='column'
              flex={1}
              gap='10px'
              minW={UI_SETTINGS.COL_MIN_W}
            ></Flex>
            <Flex
              direction='column'
              flex={1}
              gap='10px'
              minW={UI_SETTINGS.COL_MIN_W}
            ></Flex>

            <Flex
              direction='column'
              flex={1}
              gap='10px'
              minW={UI_SETTINGS.COL_MIN_W}
            ></Flex>
          </Flex>

          {/* Text Step 1 */}
          {step === 1 && (
            <Flex
              align='center'
              gap={'20px'}
              left={['120px', '150px']}
              position='absolute'
              top={['110px', '80px']}
              w={'100%'}
              zIndex={4}
            >
              <Flex direction='column' gap={'30px'}>
                <TopArrow />
                <BottomArrow />
              </Flex>

              <Box textAlign='center' w={['136px', '250px']}>
                <Text
                  fontFamily='Antonio'
                  fontSize={['16px', '40px']}
                  fontWeight='700'
                  lineHeight='52px'
                  mb={[0, '10px']}
                >
                  {t(tKeys.startHere)}!
                </Text>
                <Text
                  fontFamily='Averta'
                  fontSize={['12px', '16px']}
                  fontWeight={700}
                >
                  {t(tKeys.selectWhichTeamYouThinkWillWinRound1)}
                </Text>
              </Box>
            </Flex>
          )}

          {step === 2 && <SecondStepText />}

          {step === 2 && (
            <Box position='absolute' top={['-90px']} w={'100%'}>
              <StatusBar />
            </Box>
          )}

          {step === 2 && isMobile && (
            <Box left='40vw' position='absolute' top={'465px'}>
              <MobileArrow2 />{' '}
            </Box>
          )}
        </Box>
      }
    </>
  );
};

const TopArrow = () => (
  <svg
    fill='none'
    height='36'
    viewBox='0 0 43 36'
    width='43'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1.41073 5.31641C1.66188 4.94352 1.98031 4.62069 2.34972 4.36444L2.39788 4.34387C2.49583 4.27993 2.5984 4.22334 2.70473 4.17458L2.92909 4.07851C3.04387 4.0358 3.15429 3.98217 3.25881 3.91835L3.30698 3.89782C3.44434 3.82007 3.77647 3.62098 4.27789 3.33047L4.33517 3.28703C4.4886 3.20242 5.0771 2.89357 6.0778 2.3514L6.10981 2.33765C6.57701 2.09973 6.93195 1.90998 7.2526 1.7727L7.26846 1.76589C7.97036 1.46541 8.68994 1.20804 9.42319 0.995216C9.75743 0.890149 10.0918 0.784972 10.4329 0.695765L10.465 0.682016C11.5984 0.405654 12.3449 0.19993 12.7021 0.103809C13.3604 -0.0884172 14.0681 -0.0116486 14.67 0.317272C15.2718 0.646194 15.7186 1.20041 15.9123 1.85832C16.106 2.51624 16.0308 3.22414 15.7033 3.82671C15.3757 4.42928 14.8225 4.87731 14.1651 5.07252C13.7598 5.18907 12.9424 5.40629 11.7609 5.70333C11.4906 5.78109 11.2045 5.86556 10.9343 5.94327L10.9183 5.95018C10.5222 6.06922 10.133 6.21046 9.7528 6.37325C16.0113 7.71562 21.9062 10.3937 27.0342 14.2243C32.273 17.9561 37.2697 23.6628 42.0245 31.3443C42.3837 31.9292 42.4962 32.6326 42.3374 33.3003C42.1785 33.9679 41.7614 34.5454 41.1773 34.9058C40.5933 35.2663 39.8902 35.3804 39.2221 35.2231C38.5541 35.0658 37.9757 34.6499 37.6139 34.0667C33.2146 26.979 28.6808 21.7633 24.0126 18.4195L23.9669 18.4011C19.5428 15.0916 14.4747 12.7451 9.08884 11.5126C10.6444 13.6857 12.0595 15.9559 13.3257 18.3094C13.4829 18.6111 13.579 18.9408 13.6086 19.2796C13.6381 19.6185 13.6006 19.9598 13.4982 20.2842C13.3957 20.6085 13.2303 20.9095 13.0114 21.1698C12.7926 21.4302 12.5245 21.6448 12.2226 21.8015C11.9207 21.9581 11.5908 22.0537 11.252 22.0828C10.9131 22.1119 10.5718 22.0739 10.2476 21.9709C9.9234 21.868 9.62269 21.7021 9.36267 21.4828C9.10264 21.2636 8.8884 20.9952 8.7322 20.693C7.10194 17.7356 5.26561 14.8965 3.23679 12.1968L3.17959 12.1075C2.68737 11.4252 2.15486 10.7729 1.58491 10.154L1.52761 10.0646C1.37438 9.88379 1.27133 9.77612 1.20491 9.70971C0.820058 9.4762 0.505269 9.14326 0.293687 8.74593C0.0821045 8.3486 -0.0184434 7.90158 0.00262135 7.45192C0.0236861 7.00226 0.165585 6.56659 0.413384 6.19078C0.661183 5.81497 1.00573 5.51291 1.41073 5.31641Z'
      fill='white'
    />
    <path
      d='M9.42203 0.98888L9.42676 0.987305L9.42046 0.98888L9.42203 0.98888Z'
      fill='white'
    />
  </svg>
);

const BottomArrow = () => (
  <svg
    fill='none'
    height='36'
    viewBox='0 0 43 36'
    width='43'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1.41073 30.0566C1.66188 30.4295 1.98031 30.7524 2.34972 31.0086L2.39788 31.0292C2.49583 31.0931 2.5984 31.1497 2.70473 31.1985L2.92909 31.2945C3.04387 31.3372 3.15429 31.3909 3.25881 31.4547L3.30698 31.4752C3.44434 31.553 3.77647 31.7521 4.27789 32.0426L4.33517 32.086C4.4886 32.1706 5.0771 32.4795 6.0778 33.0216L6.10981 33.0354C6.57701 33.2733 6.93195 33.4631 7.2526 33.6003L7.26846 33.6072C7.97036 33.9076 8.68994 34.165 9.42319 34.3778C9.75743 34.4829 10.0918 34.5881 10.4329 34.6773L10.465 34.691C11.5984 34.9674 12.3449 35.1731 12.7021 35.2692C13.3604 35.4615 14.0681 35.3847 14.67 35.0558C15.2718 34.7269 15.7186 34.1726 15.9123 33.5147C16.106 32.8568 16.0308 32.1489 15.7033 31.5463C15.3757 30.9438 14.8225 30.4957 14.1651 30.3005C13.7598 30.184 12.9424 29.9668 11.7609 29.6697C11.4906 29.592 11.2045 29.5075 10.9343 29.4298L10.9183 29.4229C10.5222 29.3038 10.133 29.1626 9.7528 28.9998C16.0113 27.6574 21.9062 24.9794 27.0342 21.1487C32.273 17.4169 37.2697 11.7102 42.0245 4.02872C42.3837 3.44389 42.4962 2.74047 42.3374 2.07279C42.1785 1.40511 41.7614 0.827694 41.1773 0.467216C40.5933 0.106739 39.8902 -0.00735496 39.2221 0.149955C38.5541 0.30727 37.9757 0.723148 37.6139 1.30635C33.2146 8.39404 28.6808 13.6098 24.0126 16.9536L23.9669 16.9719C19.5428 20.2814 14.4747 22.6279 9.08884 23.8604C10.6444 21.6873 12.0595 19.4171 13.3257 17.0636C13.4829 16.762 13.579 16.4323 13.6086 16.0934C13.6381 15.7546 13.6006 15.4132 13.4982 15.0889C13.3957 14.7645 13.2303 14.4636 13.0114 14.2032C12.7926 13.9429 12.5245 13.7282 12.2226 13.5716C11.9207 13.4149 11.5908 13.3193 11.252 13.2902C10.9131 13.2611 10.5718 13.2992 10.2476 13.4021C9.9234 13.5051 9.62269 13.6709 9.36267 13.8902C9.10264 14.1095 8.8884 14.3779 8.7322 14.68C7.10194 17.6375 5.26561 20.4765 3.23679 23.1762L3.17959 23.2655C2.68737 23.9478 2.15486 24.6002 1.58491 25.219L1.52761 25.3084C1.37438 25.4893 1.27133 25.5969 1.20491 25.6633C0.820058 25.8968 0.505269 26.2298 0.293687 26.6271C0.0821045 27.0244 -0.0184434 27.4715 0.00262135 27.9211C0.0236861 28.3708 0.165585 28.8065 0.413384 29.1823C0.661183 29.5581 1.00573 29.8601 1.41073 30.0566Z'
      fill='white'
    />
    <path
      d='M9.42203 34.3842L9.42676 34.3857L9.42046 34.3842L9.42203 34.3842Z'
      fill='white'
    />
  </svg>
);

const MobileArrow2 = () => (
  <svg
    fill='none'
    height='38'
    viewBox='0 0 41 38'
    width='41'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M3.49542 35.1919C3.18427 34.8674 2.93631 34.4878 2.76424 34.0724L2.75435 34.021C2.71263 33.9117 2.67908 33.7995 2.65398 33.6852L2.60767 33.4456C2.59028 33.3244 2.56128 33.2051 2.52108 33.0894L2.51123 33.038C2.46438 32.8873 2.34025 32.5205 2.16269 31.9688L2.13238 31.9036C2.08224 31.7358 1.90521 31.0952 1.58759 30.0022L1.58094 29.968C1.44752 29.461 1.33735 29.0739 1.2712 28.7314L1.2679 28.7145C1.12311 27.9648 1.02419 27.207 0.971709 26.4453C0.939914 26.0964 0.908032 25.7474 0.893206 25.395L0.886576 25.3608C0.856854 24.1946 0.814108 23.4215 0.795925 23.052C0.747689 22.3679 0.972797 21.6925 1.42187 21.1741C1.87093 20.6557 2.50729 20.3367 3.19132 20.2869C3.87536 20.2371 4.55121 20.4607 5.07061 20.9086C5.59001 21.3564 5.91054 21.9921 5.96188 22.676C5.98983 23.0968 6.02877 23.9416 6.06849 25.1593C6.08717 25.4398 6.10903 25.7374 6.12768 26.0179L6.13104 26.035C6.16336 26.4474 6.21887 26.8576 6.29732 27.2637C8.9364 21.4322 12.8037 16.2394 17.6347 12.0403C22.3926 7.71216 29.0291 4.03926 37.5443 1.02165C38.192 0.79469 38.9032 0.833916 39.5221 1.13072C40.1409 1.42753 40.6167 1.95768 40.8451 2.60486C41.0735 3.25204 41.0359 3.9634 40.7405 4.58289C40.4451 5.20237 39.916 5.67938 39.2694 5.90926C31.4099 8.70545 25.3513 12.03 21.0936 15.8829L21.066 15.9236C16.8935 19.5454 13.5256 24.0006 11.179 29.0025C13.6325 27.9432 16.1512 27.0417 18.7197 26.3034C19.0478 26.2138 19.3904 26.1898 19.7278 26.2327C20.0652 26.2757 20.3909 26.3847 20.6861 26.5536C20.9813 26.7225 21.2404 26.948 21.4484 27.2171C21.6564 27.4862 21.8093 27.7937 21.8984 28.122C21.9875 28.4502 22.011 28.7929 21.9675 29.1302C21.924 29.4676 21.8145 29.793 21.6451 30.088C21.4758 30.383 21.2499 30.6417 20.9805 30.8493C20.7111 31.0569 20.4034 31.2093 20.0749 31.2979C16.839 32.2639 13.6751 33.4564 10.6066 34.8665L10.5072 34.9035C9.73602 35.2398 8.98558 35.6219 8.25992 36.0476L8.16041 36.0847C7.9512 36.1961 7.82413 36.2739 7.74514 36.3248C7.43533 36.6513 7.0432 36.8884 6.61004 37.0109C6.17688 37.1334 5.7187 37.1368 5.28373 37.0209C4.84877 36.905 4.4531 36.6739 4.13839 36.352C3.82368 36.0302 3.60156 35.6294 3.49542 35.1919Z'
      fill='white'
    />
  </svg>
);
